import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FieldArray } from 'redux-form';
import { Grid, Field, Flex, Button, Segment, Divider } from 'nallian-shared-ui/lib/components';
import { SelectFieldValueComponent } from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import ReadOnlyFieldValueComponent from '../../../../app/components/ReadOnlyFieldValueComponent';
import PrincipalAppFeatures from './PrincipalAppFeatures';

const PrincipalAppGrid = styled(Grid)`
  width: 100%;
`;

const PrincipalAppSegment = styled(Segment)`
  width: 100%;
`;

const RemoveFlex = styled(Flex)`
  position: relative;
  z-index: 1;
`;

const PrincipalApp = ({
  apps,
  index,
  fields,
  field,
  submitProcessing,
  handleRemoveFieldClick,
  editMode,
  readOnly,
  formName,
}) => {
  const { t } = useTranslation();

  // const dispatch = useDispatch();

  const value = useMemo(() => fields.get(index), [fields, index]);
  const isNew = Boolean(value?.isNew);
  const appInstallationTag = useMemo(() => get(value?.app?.split('_'), '[0]'), [value]);
  const communityTag = useMemo(() => get(value?.app?.split('_'), '[1]'), [value]);

  const existingApps = useSelector(state =>
    get(state, `form.${formName || 'principalForm'}.values.communityApps`, [])
  );

  const filteredOptions = useMemo(
    () =>
      apps.filter(app => {
        const appHasFeaturesWithRoles = app.communityAppFeatures?.some(
          caf => caf.communityAppFeatureRoles?.length > 0
        );
        const exist = existingApps.find(existingApp => existingApp.app === app.value);
        return appHasFeaturesWithRoles && (!exist || exist?.app === value.app);
      }),
    [apps, existingApps, value]
  );

  const appFeatures = useMemo(
    () =>
      get(
        apps.find(
          app =>
            app.appInstallation?.tag === appInstallationTag && app.community?.tag === communityTag
        ),
        'communityAppFeatures',
        []
      )
        .filter(caf => (caf.communityAppFeatureRoles || []).length > 0)
        .map(caf => ({
          ...caf,
          key: caf.appInstallationFeature?.tag,
          value: caf.appInstallationFeature?.tag,
          text: caf.alias || caf.appInstallationFeature?.name,
        })),
    [apps, appInstallationTag, communityTag]
  );

  // useEffect(() => {
  // if (value.isNew) {
  // dispatch(change(formName || 'principalForm', `${field}.features`, []));
  // }
  // }, [value.app, value.isNew, dispatch, field, formName]);

  return (
    <PrincipalAppSegment secondary>
      <PrincipalAppGrid>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Flex fluid direction="column">
            <Field
              fluid
              search
              label={t('app')}
              name={`${field}.app`}
              valueComponent={
                (editMode && !isNew) || readOnly
                  ? ReadOnlyFieldValueComponent
                  : SelectFieldValueComponent
              }
              options={filteredOptions}
              disabled={submitProcessing}
            />
            {value?.app && (
              <FieldArray
                readOnly={readOnly}
                editMode={editMode}
                name={`${field}.features`}
                app={value}
                component={PrincipalAppFeatures}
                features={appFeatures}
                formName={formName}
              />
            )}
          </Flex>
        </Grid.Column>
      </PrincipalAppGrid>
      <Divider />
      {!readOnly && (
        <RemoveFlex fluid direction="column" alignItems="flex-end">
          <Button
            linkbutton
            icon="close"
            type="button"
            tabindex="-1"
            content={t('removeAppAndRoles')}
            i={index}
            onClick={handleRemoveFieldClick}
          />
        </RemoveFlex>
      )}
    </PrincipalAppSegment>
  );
};

PrincipalApp.propTypes = {
  readOnly: PropTypes.bool.isRequired,
  apps: PropTypes.array.isRequired,
  fields: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  field: PropTypes.string.isRequired,
  editMode: PropTypes.bool.isRequired,
  submitProcessing: PropTypes.bool.isRequired,
  handleRemoveFieldClick: PropTypes.func.isRequired,
  formName: PropTypes.string,
};

PrincipalApp.defaultProps = { formName: undefined };

export default PrincipalApp;
